import { Subscription } from 'rxjs';
import { AGUAppLocation, AGUCrumbItem, AGUCrumbService, DetailTrail, LabelForLocation, OriginOf } from 'agu_app_shell';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { RaCompositionState } from '../../ra-composition.state';

/** Which place a detail view is: its module, its own view key, and the view its crumb falls back to without an origin. */
export interface DetailCrumbPlace {
    readonly ModuleKey: string;
    readonly View: string;
    readonly DefaultView: string;
}

/**
 * The crumb trail of one detail view (PD §6, CP §2; P/detail.html:888): "{origin} / {record title}", where the origin is the
 * location's `From` (Home, Search, Saved, a finder tab, "Career paths") or else the module's own search view.
 *
 * Shared by the grant, job and person detail views. It follows the host's location rather than the view's lifecycle because the
 * surface keeps visited modules mounted: a detail left for another module is hidden, not destroyed, and must publish its trail
 * again when the member comes back to it. While the place on screen is this detail it publishes to the surface's `AGUCrumbService`
 * (the phone app bar reads it) — `[]` until the record on screen has loaded, so a previous record's trail never lingers.
 *
 * Every collaborator is optional: a detail rendered outside a surface simply has no crumb.
 */
export class DetailCrumb {
    /** What `agu-crumb` renders; empty until the record on screen has loaded. */
    Trail: readonly AGUCrumbItem[] = [];

    private location: AGUAppLocation | null = null;
    private loaded: { readonly RecordID: string; readonly Title: string } | null = null;
    private subscription: Subscription | null = null;

    constructor(
        private readonly place: DetailCrumbPlace,
        private readonly host: ExplorerRaHostAdapter | null,
        private readonly composition: RaCompositionState | null,
        private readonly crumbs: AGUCrumbService | null
    ) {}

    /** Begin following the host's location (it replays the current one). */
    Start(): void {
        if (!this.host || this.subscription) return;
        this.subscription = this.host.Location$.subscribe((loc) => this.OnLocation(loc));
    }

    /** The record `recordID` finished loading with this title; a response for another record is ignored. */
    Loaded(recordID: string, title: string): void {
        this.loaded = { RecordID: recordID, Title: title };
        this.Rebuild();
        if (this.location) this.crumbs?.Publish(this.Trail);
    }

    /** The crumb's `Navigate`: go to the ancestor through the host. */
    Navigate(location: AGUAppLocation): void {
        this.host?.NavigateTo(location);
    }

    /** Leave for the origin — the first ancestor — as "Not for me" does after a dismiss. */
    Back(): void {
        if (!this.host) return;
        this.host.NavigateTo(OriginOf(this.location ?? this.host.CurrentLocation, this.place.DefaultView));
    }

    /** The view is going: stop following the location and withdraw the trail. */
    Stop(): void {
        this.subscription?.unsubscribe();
        this.subscription = null;
        this.location = null;
        this.crumbs?.Publish([]);
    }

    private OnLocation(loc: AGUAppLocation): void {
        const isThisDetail = loc.ModuleKey === this.place.ModuleKey && loc.View === this.place.View;
        if (!isThisDetail) {
            // Hidden behind another place: nothing to publish. The surface ignores a stale trail off detail views.
            this.location = null;
            return;
        }
        this.location = loc;
        this.Rebuild();
        this.crumbs?.Publish(this.Trail);
    }

    private Rebuild(): void {
        const loc = this.location;
        const loaded = this.loaded;
        if (!loc || !loaded || loaded.RecordID !== (loc.RecordID ?? '')) {
            this.Trail = [];
            return;
        }
        const origin = OriginOf(loc, this.place.DefaultView);
        this.Trail = DetailTrail(LabelForLocation(this.composition?.Modules ?? [], origin), origin, loaded.Title);
    }
}
